/**
 * Performance Monitoring Utilities
 * Tracks Web Vitals and custom performance metrics
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Performance thresholds (in milliseconds)
export const PERFORMANCE_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  FID: { good: 100, needsImprovement: 300 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};

// Rating helper
const getRating = (value, thresholds) => {
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsImprovement) return 'needs-improvement';
  return 'poor';
};

// Store metrics for batching
let metricsQueue = [];
let flushTimer = null;

/**
 * Send metrics to analytics endpoint
 */
const sendMetrics = async (metrics) => {
  try {
    // In production, send to your analytics backend
    const endpoint = process.env.REACT_APP_ANALYTICS_ENDPOINT;
    
    if (endpoint) {
      // Use sendBeacon for reliability (works even when page is unloading)
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(metrics)], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
      } else {
        // Fallback to fetch
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(metrics),
          keepalive: true,
        });
      }
    }
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      // Performance metrics are intentionally logged to console for monitoring
      console.group('📊 Performance Metrics');
      metrics.forEach(metric => {
        const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
        console.log(`${emoji} ${metric.name}: ${metric.value.toFixed(2)}${metric.unit || 'ms'} (${metric.rating})`);
      });
      console.groupEnd();
    }
  } catch (error) {
    // Performance monitoring errors are intentionally logged to console
    console.error('Failed to send metrics:', error);
  }
};

/**
 * Flush metrics queue
 */
const flushMetrics = () => {
  if (metricsQueue.length > 0) {
    sendMetrics([...metricsQueue]);
    metricsQueue = [];
  }
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

/**
 * Add metric to queue and schedule flush
 */
const queueMetric = (metric) => {
  metricsQueue.push(metric);
  
  // Flush after 5 seconds or when queue reaches 10 metrics
  if (metricsQueue.length >= 10) {
    flushMetrics();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushMetrics, 5000);
  }
};

/**
 * Initialize Web Vitals tracking
 */
export const initWebVitals = () => {
  // Largest Contentful Paint (LCP)
  getLCP((metric) => {
    const rating = getRating(metric.value, PERFORMANCE_THRESHOLDS.LCP);
    queueMetric({
      name: 'LCP',
      value: metric.value,
      rating,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
    });
  });

  // First Input Delay (FID)
  getFID((metric) => {
    const rating = getRating(metric.value, PERFORMANCE_THRESHOLDS.FID);
    queueMetric({
      name: 'FID',
      value: metric.value,
      rating,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
    });
  });

  // Cumulative Layout Shift (CLS)
  getCLS((metric) => {
    const rating = getRating(metric.value, PERFORMANCE_THRESHOLDS.CLS);
    queueMetric({
      name: 'CLS',
      value: metric.value,
      rating,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
      unit: '',
    });
  });

  // First Contentful Paint (FCP)
  getFCP((metric) => {
    const rating = getRating(metric.value, PERFORMANCE_THRESHOLDS.FCP);
    queueMetric({
      name: 'FCP',
      value: metric.value,
      rating,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
    });
  });

  // Time to First Byte (TTFB)
  getTTFB((metric) => {
    const rating = getRating(metric.value, PERFORMANCE_THRESHOLDS.TTFB);
    queueMetric({
      name: 'TTFB',
      value: metric.value,
      rating,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
    });
  });

  // Flush metrics before page unload
  window.addEventListener('beforeunload', flushMetrics);
  
  // Flush metrics when page becomes hidden
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushMetrics();
    }
  });
};

/**
 * Track custom performance metric
 */
export const trackPerformance = (metricName, value, metadata = {}) => {
  queueMetric({
    name: metricName,
    value,
    timestamp: Date.now(),
    ...metadata,
  });
};

/**
 * Measure function execution time
 */
export const measureExecutionTime = async (name, fn) => {
  const startTime = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - startTime;
    trackPerformance(`execution_${name}`, duration, { type: 'execution' });
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    trackPerformance(`execution_${name}`, duration, { type: 'execution', error: true });
    throw error;
  }
};

/**
 * Track resource loading performance
 */
export const trackResourceTiming = () => {
  if (!window.PerformanceObserver) return;

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.entryType === 'resource') {
        // Track slow resources (> 1 second)
        if (entry.duration > 1000) {
          queueMetric({
            name: 'slow_resource',
            value: entry.duration,
            resourceUrl: entry.name,
            resourceType: entry.initiatorType,
            timestamp: Date.now(),
          });
        }
      }
    });
  });

  observer.observe({ entryTypes: ['resource'] });
};

/**
 * Track navigation timing
 */
export const trackNavigationTiming = () => {
  if (!window.PerformanceObserver) return;

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.entryType === 'navigation') {
        queueMetric({
          name: 'navigation_timing',
          value: entry.loadEventEnd - entry.fetchStart,
          domContentLoaded: entry.domContentLoadedEventEnd - entry.fetchStart,
          domInteractive: entry.domInteractive - entry.fetchStart,
          timestamp: Date.now(),
        });
      }
    });
  });

  observer.observe({ entryTypes: ['navigation'] });
};

/**
 * Track long tasks (> 50ms)
 */
export const trackLongTasks = () => {
  if (!window.PerformanceObserver) return;

  try {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration > 50) {
          queueMetric({
            name: 'long_task',
            value: entry.duration,
            startTime: entry.startTime,
            timestamp: Date.now(),
          });
        }
      });
    });

    observer.observe({ entryTypes: ['longtask'] });
  } catch (error) {
    // longtask may not be supported in all browsers
    // Long task monitoring warnings are intentionally logged to console
    console.warn('Long task monitoring not supported:', error);
  }
};

/**
 * Get current performance metrics snapshot
 */
export const getPerformanceSnapshot = () => {
  const navigation = performance.getEntriesByType('navigation')[0];
  const paint = performance.getEntriesByType('paint');
  
  return {
    navigation: navigation ? {
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
      loadComplete: navigation.loadEventEnd - navigation.fetchStart,
      domInteractive: navigation.domInteractive - navigation.fetchStart,
    } : null,
    paint: paint.reduce((acc, entry) => {
      acc[entry.name] = entry.startTime;
      return acc;
    }, {}),
    memory: performance.memory ? {
      usedJSHeapSize: performance.memory.usedJSHeapSize,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
    } : null,
  };
};

export default {
  initWebVitals,
  trackPerformance,
  measureExecutionTime,
  trackResourceTiming,
  trackNavigationTiming,
  trackLongTasks,
  getPerformanceSnapshot,
};
